import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Card, 
  CardContent, 
  CardMedia, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useSpotify } from '../context';
import Layout from '../components/layout/Layout';
import { useNavigate } from 'react-router-dom';

const Playlists: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { playlists, isLoading, error, refreshPlaylists, createPlaylist } = useSpotify();
  const navigate = useNavigate();
  
  const [openDialog, setOpenDialog] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleRefresh = async () => {
    await refreshPlaylists();
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewPlaylistName('');
    setNewPlaylistDescription('');
  };

  const handleCreatePlaylist = async () => {
    if (!newPlaylistName.trim()) return;
    
    setIsCreating(true);
    try {
      const playlist = await createPlaylist(
        newPlaylistName,
        newPlaylistDescription || `Created with Tunes with Otto`
      );
      
      handleCloseDialog();
      // Navigate to the new playlist
      navigate(`/playlist/${playlist.id}`);
    } catch (err) {
      console.error('Error creating playlist:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handlePlaylistClick = (playlistId: string) => {
    navigate(`/playlist/${playlistId}`);
  };

  return (
    <Layout>
      <Box sx={{ mb: 4 }} className="playlists-container">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          mb: 3 
        }}
        className="playlists-header">
          <Typography variant="h4" component="h1" sx={{ mb: isMobile ? 2 : 0 }}>
            Your Playlists
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'space-between' : 'flex-end'
          }}
          className="playlists-actions">
            <Button 
              startIcon={<RefreshIcon />} 
              onClick={handleRefresh}
              disabled={isLoading}
              sx={{ mr: 1 }}
              size={isMobile ? "small" : "medium"}
            >
              Refresh
            </Button>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              size={isMobile ? "small" : "medium"}
            >
              Create Playlist
            </Button>
          </Box>
        </Box>
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ my: 2 }}>
            {error}
          </Typography>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            mx: isMobile ? -0.5 : -1 
          }}
          className="playlists-grid">
            {playlists.length > 0 ? (
              playlists.map((playlist) => (
                <Box 
                  key={playlist.id} 
                  sx={{ 
                    width: { 
                      xs: isMobile ? '50%' : '100%', 
                      sm: '50%', 
                      md: '33.33%', 
                      lg: '25%' 
                    }, 
                    padding: isMobile ? 0.5 : 1 
                  }}
                  onClick={() => handlePlaylistClick(playlist.id)}
                  className="playlist-item"
                >
                  <Card 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      height: '100%',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 6
                      }
                    }}
                  >
                    {playlist.imageUrl ? (
                      <CardMedia
                        component="img"
                        height={isMobile ? "120" : "160"}
                        image={playlist.imageUrl}
                        alt={playlist.name}
                      />
                    ) : (
                      <Box 
                        className="placeholder-playlist"
                        sx={{ 
                          height: isMobile ? 120 : 160, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center' 
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">Playlist</Typography>
                      </Box>
                    )}
                    <CardContent sx={{ 
                      flexGrow: 1, 
                      py: isMobile ? 1 : 2,
                      px: isMobile ? 1.5 : 2,
                      '&:last-child': { pb: isMobile ? 1 : 2 }
                    }}>
                      <Typography 
                        variant={isMobile ? "body1" : "subtitle1"} 
                        component="div" 
                        noWrap
                        sx={{ fontWeight: 'medium' }}
                      >
                        {playlist.name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        noWrap
                        sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
                      >
                        {playlist.description || 'No description'}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? '0.7rem' : undefined }}
                      >
                        {playlist.totalTracks} tracks
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))
            ) : (
              <Box sx={{ width: '100%', textAlign: 'center', my: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  You don't have any playlists yet. Create one to get started!
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Create Playlist Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create New Playlist</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Playlist Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newPlaylistName}
            onChange={(e) => setNewPlaylistName(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{
              style: { fontSize: isMobile ? '0.95rem' : undefined }
            }}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={isMobile ? 2 : 3}
            value={newPlaylistDescription}
            onChange={(e) => setNewPlaylistDescription(e.target.value)}
            inputProps={{
              style: { fontSize: isMobile ? '0.95rem' : undefined }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: isMobile ? 2 : 3, pb: isMobile ? 2 : 3 }}>
          <Button onClick={handleCloseDialog} size={isMobile ? "small" : "medium"}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreatePlaylist} 
            variant="contained"
            disabled={isCreating || !newPlaylistName.trim()}
            size={isMobile ? "small" : "medium"}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Playlists;
